import {hot} from "react-hot-loader"
import React, {useEffect, useRef, useState} from "react"
import css from "./App.css"
import useStarfishAPI from "../hooks/useStarfishAPI"
import Toast from 'common/Toast'
import PropTypes from "prop-types"
import DataExtractView from "./DataExtractView"
import {dateFormat} from "./utils"
import ConfirmDialogs from "./ConfirmDialogs"
import ErrorMessage from "../common/ErrorMessage"

const URL = '../api/self-service-reporting'

/**
 * Create the Data Extracts screen that allows tenant admins to manage their tenant's Starfish data extracts.
 * Tenant admins are able to enable/disable data extracts, manage reports to include in data extracts, and manage
 * users/access keys that can access the extracted data in AWS S3.
 *
 * @returns {JSX.Element} The tenant admin Data Extracts page
 */
const App = ({tenantId}) => {

    // UI logging
    const [toastMessage, setToastMessage] = useState("");

    // API calls to the Kotlin code
    const [responseWrite, callWriteAPI] = useStarfishAPI({isNewAPI: true});
    const [responseRead, callReadAPI] = useStarfishAPI({isNewAPI: true});
    const [responseCreateUser, callCreateUserAPI] = useStarfishAPI({isNewAPI: true});
    const [responseDeleteUser, callDeleteUserAPI] = useStarfishAPI({isNewAPI: true});
    const [responseUpdateAccessKeyStatus, callUpdateAccessKeyStatus] = useStarfishAPI({isNewAPI: true});

    const [bucketName, setBucket] = useState("");
    const [lastExtractDate, setLastExtractDate] = useState("");
    const [tenantEnabled, setTenantEnabled] = useState(false);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    // Parameters to manage the Files tab
    const [reports, setReports] = useState([]);
    const savedReports = useRef(reports);

    // Parameters to manage the Access tab
    const [users, setUsers] = useState([]);
    const [deleteUserName, setDeleteUserName] = useState("");
    const [userCredentials, setUserCredentials] = useState(null)
    const [userCreated, setUserCreated] = useState(false)
    const [getDataSuccess, setGetDataSuccess] = useState(false)
    const [saveTenantEnabledFlagSuccess, setSaveTenantEnabledFlagSuccess] = useState(true)
    const [selectedOption, setSelectedOption] = useState({
        value: "general_data_extracts",
        label: "General Data Extracts"
    });
    const [maxAccessKeys, setMaxAccessKeys] = useState(10);
    const [showSecretKey, setShowSecretKey] = useState(false);
    const [accessKeyStatus, setAccessKeyStatus] = useState({
        userName: "",
        status: "",
        statusVerb: ""
    })

    // Modals
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenNewAccessKey, setIsOpenNewAccessKey] = useState(false);
    const [isOpenDeleteAccessKey, setIsOpenDeleteAccessKey] = useState(false);
    const [isOpenUpdateAccessKeyStatus, setIsOpenUpdateAccessKeyStatus] = useState(false);


    /**
     * Function that handles the show/hide secret access key button
     */
    const onClickShowSecretKey = () => {
        setShowSecretKey(!showSecretKey)
    }

    /**
     * Function that manages the report checkboxes
     * @param {number} index The index of the report being changed in the array of reports
     * @param {bool} enabled Value to set the report's checkbox to
     */
    const onReportCheckboxChange = (index, enabled) => {
        setReports(arr => {
            return [
                ...arr.slice(0, index),
                {...arr[index], enabled},
                ...arr.slice(index + 1)
            ]
        })
    }

    /**
     * Function that manages updating the access key status when the checkbox is clicked.
     * @param {string} userName The IAM username associated with the access key
     * @param {string} status The access key status before the checkbox is clicked
     */
    const onAccessKeyStatusChange = (userName, status) => {
        if (status === 'Active')
            setAccessKeyStatus({
                userName: userName,
                status: "Inactive",
                statusVerb: "Inactivate"
            })
        else
            setAccessKeyStatus({
                userName: userName,
                status: "Active",
                statusVerb: "Activate"
            })
    }

    /**
     * Deletes the self service user from AWS. This will remove the user's access key from the UI's list of access keys.
     * @param {string} userName The user to delete
     */
    const deleteUser = (userName) => {
        callDeleteUserAPI(URL + "/delete-user", {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                    "user_name": userName
                }
            )
        })

        // close delete confirmation modal
        setIsOpenDeleteAccessKey(false)
    }


    /**
     * Updates an access key's status in AWS.
     * @param {string} userName The IAM username who is associated with the access key being updated.
     * @param {string} status The new access key status to save in AWS
     */
    const updateAccessKeyStatus = (userName, status) => {
        callUpdateAccessKeyStatus(URL + "/update-access-key-status", {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                    "user_name": userName,
                    "access_key_status": status
                }
            )
        })

        // close update access key confirmation modal
        setIsOpenUpdateAccessKeyStatus(false)
    }

    /**
     * Set all report checkboxes to the value of the select all checkbox
     *
     * @param {bool} enabled status to set all the report checkboxes to
     */
    const onSelectAllCheckboxChange = (enabled) => {
        setReports(reports.map(report => {
                    return {...report, enabled: enabled}
                }
            )
        )
    }

    /**
     * Function that creates a new AWS IAM user with permissions to download files from the tenant's s3 bucket.
     * Only the user's access key will be displayed on the UI since tenant admins do not care about the username.
     */
    const createUser = () => {
        callCreateUserAPI(URL + "/create-user", {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                    "user_type": selectedOption.value,
                    "user_count": users.length
                }
            )
        })
    }

    /**
     * Function that gets all of the display data from the database and AWS for the Data Extracts Tenant Admin page.
     */
    const getDisplayData = () => {
        setUserCreated(false)
        callReadAPI(URL + '?tenantId=' + tenantId, {
            method: "GET",
            headers: {
                Pragma: "no-cache",
                "Cache-Control": "no-cache",
                Expires: "-1"
            }
        })
    }

    /*
     * Function that cancels create a new access key
     *
     */
    const onNewAccessKeyCancel = () => {
        setIsOpenNewAccessKey(!isOpenNewAccessKey)
        setUserCredentials(null)
        setUserCreated(false)
        setShowSecretKey(false)
    }

    /**
     * Clears report file checkbox selections that have been made since the last time the page was saved or rendered.
     */
    const clear = () => {
        setReports(savedReports.current)
        setIsOpen(false);
    }

    /**
     * Format the access key created date format for all users to be UI friendly.
     * @param {Array} jsonUsers The list of users and their credential details defined in API responses.
     */
    const formatUserCreateDates = (jsonUsers) => {
        for (const index in jsonUsers) {
            jsonUsers[index].access_key_created_date
                = dateFormat(jsonUsers[index].access_key_created_date)
        }
    }

    /**
     * Save tenant enabled flag to the backend after the tenant admin has clicked save.
     * @param {bool} isTenantEnabled flag to determine if tenant is enabled
     */
    const saveTenantEnabledFlag = (isTenantEnabled) => {
        let bucketPath = null
        if (bucketName !== "") {
            bucketPath = bucketName
        }
        callWriteAPI(URL, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                "bucket_path": bucketPath,
                "enabled": isTenantEnabled,
                "reports": reports
            })
        })
    }

    /**
     * Save reports changes to the backend after the tenant admin has clicked save.
     */
    const saveReports = () => {
        let bucketPath = null
        if (bucketName !== "") {
            bucketPath = bucketName
        }
        callWriteAPI(URL, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                "bucket_path": bucketPath,
                "enabled": tenantEnabled,
                "reports": reports
            })
        })
    }

    /**
     * Reads self service reporting data from the backend.
     */
    useEffect(() => {
        getDisplayData()
    }, [])

    /**
     * After reading self service reporting data from the backend, process the response.
     */
    useEffect(() => {
        if (responseRead.success) {
            setGetDataSuccess(true)
            setTenantEnabled(responseRead.data.enabled)
            setReports(responseRead.data.reports)
            savedReports.current = responseRead.data.reports

            formatUserCreateDates(responseRead.data.users)
            setUsers(responseRead.data.users)

            // Only set bucket path, last extract date, and max access keys if not null
            if (responseRead.data.bucket_path !== null) {
                setBucket(responseRead.data.bucket_path)
            }
            if (responseRead.data.last_extract_date !== null) {
                setLastExtractDate(dateFormat(responseRead.data.last_extract_date))
            }
            if (responseRead.data.max_access_keys !== null)
            {
                setMaxAccessKeys(responseRead.data.max_access_keys)
            }
        }
    }, [responseRead])

    /**
     * After saving changes to the backend, process the response.
     */
    useEffect(() => {
        if (responseWrite.success) {
            setSaveTenantEnabledFlagSuccess(true)
            setBucket(responseWrite.data.bucket_path)
            setTenantEnabled(responseWrite.data.enabled)
            savedReports.current = reports

            formatUserCreateDates(responseWrite.data.users)
            setUsers(responseWrite.data.users)

            setToastMessage("Configuration successfully updated.")
        }
    }, [responseWrite])

    /**
     * After creating and saving a user, process the response.
     */
    useEffect(() => {
        if (responseCreateUser.success) {
            if (responseCreateUser.data.message) {
                onNewAccessKeyCancel()
            } else {
                setUserCredentials(responseCreateUser.data)
                setUsers(() => [...users,
                    {
                        user_name: responseCreateUser.data.user_name,
                        user_type: responseCreateUser.data.user_type,
                        access_key: responseCreateUser.data.access_key,
                        access_key_status: responseCreateUser.data.access_key_status,
                        access_key_created_date: dateFormat(responseCreateUser.data.access_key_created_date)
                    }]
                )
                setUserCreated(true)
                setToastMessage("Access key successfully created.");
            }
        }
    }, [responseCreateUser]);

    /**
     * After deleting a user, remove the user from the list of users. This will remove the access key from the
     * list of keys displayed.
     */
    useEffect(() => {
        if (responseDeleteUser.success) {
            setUsers(arr => {
                return arr.filter(user => user.user_name !== responseDeleteUser.data.user_name)
                }
            )
            setToastMessage("Access key successfully deleted.");
        }
    }, [responseDeleteUser]);

    /**
     * After updating an access key's status in AWS, process the response. This will update the new access key status
     * in the users state.
     */
    useEffect(() => {
        if (responseUpdateAccessKeyStatus.success) {
            const index = users.findIndex(user => user.user_name === responseUpdateAccessKeyStatus.data.user_name)
            setUsers(arr => {
                return [
                    ...arr.slice(0, index),
                    {...arr[index], access_key_status: responseUpdateAccessKeyStatus.data.access_key_status},
                    ...arr.slice(index + 1)
                ]
            })

            setToastMessage("Access key successfully updated.");
        }
    }, [responseUpdateAccessKeyStatus]);

    /**
     * @return {JSX.Element|null} Returns the notification banner component with an error message. Returns null
     * (aka no banner) if there are no errors.
     */
    const getError = () => {
        const apiError = responseWrite.error
                            || responseRead.error
                            || responseCreateUser.error
                            || responseDeleteUser.error
                            || responseUpdateAccessKeyStatus.error

        let errorMessage = null

        if (apiError)
            errorMessage = apiError
        else if (responseCreateUser.success && responseCreateUser.data.message)
            errorMessage = responseCreateUser.data.message

        if (errorMessage !== null)
            return <ErrorMessage message={errorMessage}/>
        else return null
    }

    /*
     * Get the contents of the Data Extracts screen.
     * @returns {JSX.Element} Component containing the contents of tenant admin data extracts page
     */
    const getContent = () => {
        return (

            <div className={css["container"]}>
                {getError()}
                <div className={css["container-padding"]}>
                <h3 className={css["sf-admin-title"]}>Manage Data Extracts</h3>
                <hr/>
                <DataExtractView
                    getDataSuccess={getDataSuccess}
                    bucketName={bucketName}
                    tenantEnabled={tenantEnabled}
                    setTenantEnabled={setTenantEnabled}
                    reportList={reports}
                    onReportChange={onReportCheckboxChange}
                    onSelectAllChange={onSelectAllCheckboxChange}
                    lastExtractDate={lastExtractDate}
                    isOpenNewAccessKey={isOpenNewAccessKey}
                    setIsOpenNewAccessKey={setIsOpenNewAccessKey}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    onNewAccessKeyCancel={onNewAccessKeyCancel}
                    createUser={createUser}
                    userCreated={userCreated}
                    userCredentials={userCredentials}
                    userList={users}
                    setIsOpenDeleteAccessKey={setIsOpenDeleteAccessKey}
                    setDeleteUserName={setDeleteUserName}
                    maxAccessKeys={maxAccessKeys}
                    selectedTabIndex={selectedTabIndex}
                    setSelectedTabIndex={setSelectedTabIndex}
                    showSecretKey={showSecretKey}
                    onClickShowSecretKey={onClickShowSecretKey}
                    setIsOpenUpdateAccessKeyStatus={setIsOpenUpdateAccessKeyStatus}
                    onAccessKeyStatusChange={onAccessKeyStatusChange}
                    setToastMessage={setToastMessage}
                    saveTenantEnabledFlag={saveTenantEnabledFlag}
                    setSaveTenantEnabledFlagSuccess={setSaveTenantEnabledFlagSuccess}
                    saveTenantEnabledFlagSuccess={saveTenantEnabledFlagSuccess}
                    setIsOpen={setIsOpen}
                    saveReports={saveReports}
                />
                {toastMessage && <Toast message={toastMessage} onTimeout={() => setToastMessage("")}/>}
                <ConfirmDialogs
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    clear={clear}
                    isOpenDeleteAccessKey={isOpenDeleteAccessKey}
                    setIsOpenDeleteAccessKey={setIsOpenDeleteAccessKey}
                    deleteUserName={deleteUserName}
                    deleteUser={deleteUser}
                    accessKeyStatus={accessKeyStatus}
                    isOpenUpdateAccessKeyStatus={isOpenUpdateAccessKeyStatus}
                    setIsOpenUpdateAccessKeyStatus={setIsOpenUpdateAccessKeyStatus}
                    updateAccessKeyStatus={updateAccessKeyStatus}/>
                </div>
            </div>
        )
    }

    return getContent()
}

App.propTypes = {
    tenantId: PropTypes.number.isRequired
};

export default hot(module)(App)
